/**
 * PDF Helpers - Funciones auxiliares para generación de PDFs con jsPDF
 * 
 * Este módulo proporciona funciones helper para generar PDFs con texto nativo
 * usando jsPDF y jspdf-autotable, resolviendo problemas de paginación.
 */

import jsPDF from 'jspdf';
import 'jspdf-autotable';
import html2canvas from 'html2canvas';

/**
 * Texto seguro para jsPDF (Helvetica / WinAnsi).
 * Caracteres fuera de Latin-1 (bullets ●/•, subíndices, etc.) corrompen el PDF
 * (síntoma típico: "&e&E&x&a&m&e&n&" o "%Ï&" en la salida).
 */
export function normalizarTextoParaPdf(texto) {
  if (texto == null || texto === '') return '';
  let s = String(texto);

  // Separadores de línea Unicode → espacio (luego se colapsan)
  s = s.replace(/[\u2028\u2029]/g, ' ');

  // Bullets / viñetas → guion ASCII (jsPDF Helvetica no los soporta)
  s = s.replace(/[\u2022\u2023\u2043\u25CF\u25CB\u25A0\u25A1\u25AA\u25AB\u25B6\u25B8\u25E6\u2219\u00B7\u2024\u30FB]/g, '-');

  // Guiones tipográficos
  s = s.replace(/[\u2010\u2011\u2012\u2013\u2014\u2015\u2212]/g, '-');

  // Comillas tipográficas
  s = s.replace(/[\u2018\u2019\u201A\u201B]/g, "'");
  s = s.replace(/[\u201C\u201D\u201E\u201F]/g, '"');

  // Ellipsis y espacios especiales
  s = s.replace(/\u2026/g, '...');
  s = s.replace(/[\u00A0\u2000-\u200A\u202F\u205F]/g, ' ');

  // Subíndices / superíndices → ASCII
  const subMap = {
    '\u2080': '0', '\u2081': '1', '\u2082': '2', '\u2083': '3', '\u2084': '4',
    '\u2085': '5', '\u2086': '6', '\u2087': '7', '\u2088': '8', '\u2089': '9',
  };
  const supMap = {
    '\u00B9': '1', '\u00B2': '2', '\u00B3': '3',
    '\u2070': '0', '\u2074': '4', '\u2075': '5', '\u2076': '6',
    '\u2077': '7', '\u2078': '8', '\u2079': '9',
  };
  s = s.replace(/[\u2080-\u2089]/g, (ch) => subMap[ch] || '');
  s = s.replace(/[\u00B9\u00B2\u00B3\u2070\u2074-\u2079]/g, (ch) => supMap[ch] || '');

  // Marcas comerciales fuera de WinAnsi seguro
  s = s.replace(/\u2122/g, '(TM)');
  s = s.replace(/\u00AE/g, '(R)');

  try {
    s = s.replace(/\p{Extended_Pictographic}/gu, '');
  } catch (e) {
    s = s.replace(/(?:[\u2700-\u27BF]|\uD83C[\uD000-\uDFFF]|\uD83D[\uD000-\uDFFF]|\uD83E[\uD000-\uDFFF])/g, '');
  }
  s = s.replace(/[\uFE00-\uFE0F\u200B\u200C\u200D\uFEFF]/g, '');
  s = s.replace(/[\uE000-\uF8FF]/g, '');
  try {
    s = s.normalize('NFKC');
  } catch (e) {
    /* ignore */
  }

  // Cualquier carácter restante fuera de Latin-1 (0x00-0xFF) se omite:
  // Helvetica/WinAnsi no puede dibujarlos y jsPDF genera basura.
  s = s.replace(/[^\x09\x0A\x0D\x20-\x7E\xA0-\xFF]/g, '');

  s = s.replace(/\s+/g, ' ').trim();
  return s;
}

/**
 * Celdas de tabla etiqueta-valor: preserva saltos de línea y convierte marcas HTML
 * habituales (<br>, párrafos) a saltos antes de normalizar línea a línea.
 * (normalizarTextoParaPdf colapsa \n en espacio; no debe usarse directo en valores largos.)
 */
export function prepararTextoCeldaTablaPdf(texto) {
  if (texto == null || texto === '') return '';
  let s = String(texto);
  s = s.replace(/\r\n/g, '\n');
  s = s.replace(/<br\s*\/?>/gi, '\n');
  s = s.replace(/<\/p>\s*/gi, '\n');
  s = s.replace(/<p[^>]*>/gi, '');
  return s
    .split('\n')
    .map((linea) => normalizarTextoParaPdf(linea.replace(/<[^>]+>/g, ' ')))
    .join('\n');
}

/**
 * Quita del HTML lo que no se puede representar bien en PDF (Font Awesome, SVG, etc.).
 */
export function sanitizarHtmlParaPdf(html) {
  if (!html || typeof html !== 'string') return '';
  let s = html;
  s = s.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  s = s.replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '');
  s = s.replace(/<svg\b[^>]*>[\s\S]*?<\/svg>/gi, '');
  s = s.replace(/<i\b[^>]*\bclass\s*=\s*["'][^"']*\bfa[srb]?[\s-][^"']*["'][^>]*>\s*<\/i>/gi, '');
  s = s.replace(/<i\b[^>]*\bclass\s*=\s*["'][^"']*(?:fa-solid|fa-regular|fa-brands)[^"']*["'][^>]*>\s*<\/i>/gi, '');
  s = s.replace(/<span\b[^>]*\bclass\s*=\s*["'][^"']*\bfa[srb]?[\s-][^"']*["'][^>]*>\s*<\/span>/gi, '');
  s = s.replace(/&nbsp;/gi, ' ');
  return s;
}

/**
 * Convierte HTML a texto plano preservando estructura
 * 
 * @param {string} html - Contenido HTML
 * @param {Object} [options]
 * @param {boolean} [options.compacto] - Si true, reduce saltos tras p/div y br (p. ej. HTML de CKEditor en fórmulas).
 * @returns {Array<Object>} Array de bloques de texto con formato
 * 
 * Ejemplo de retorno:
 * [
 *   { texto: "Título", tipo: "titulo", negrita: true, tamanoFuente: 14 },
 *   { texto: "Párrafo normal", tipo: "parrafo", negrita: false, tamanoFuente: 10 }
 * ]
 */
export function convertirHtmlATextoPlano(html, options = {}) {
  const compacto = !!options.compacto;
  try {
    // 1. Crear un elemento DOM temporal
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = sanitizarHtmlParaPdf(html || '');
    
    // 2. Array para almacenar bloques de texto
    const bloques = [];
    
    // 3. Función recursiva para procesar nodos
    function procesarNodo(nodo, contexto = {}) {
      // 3.1 Si es nodo de texto
      if (nodo.nodeType === Node.TEXT_NODE) {
        const texto = nodo.textContent.trim();
        if (texto.length > 0) {
          bloques.push({
            texto: contexto.prefijo ? contexto.prefijo + texto : texto,
            tipo: contexto.tipo || 'parrafo',
            negrita: contexto.negrita || false,
            tamanoFuente: contexto.tamanoFuente || 10,
            color: contexto.color || '#000000',
            indentacion: contexto.indentacion || 0
          });
        }
        return;
      }
      
      // 3.2 Si es nodo elemento
      if (nodo.nodeType === Node.ELEMENT_NODE) {
        const nuevoContexto = { ...contexto };
        
        // Detectar tipo de elemento y ajustar contexto
        const tagName = nodo.tagName.toLowerCase();
        
        switch (tagName) {
          case 'h1':
          case 'h2':
          case 'h3':
          case 'h4':
          case 'h5':
          case 'h6':
            nuevoContexto.tipo = 'titulo';
            nuevoContexto.negrita = true;
            nuevoContexto.tamanoFuente = 14;
            break;
            
          case 'strong':
          case 'b':
            nuevoContexto.negrita = true;
            break;
            
          case 'p':
            nuevoContexto.tipo = 'parrafo';
            break;
            
          case 'ul':
          case 'ol':
            nuevoContexto.tipo = 'lista';
            nuevoContexto.indentacion = 5;
            break;
            
          case 'li': {
            // Procesar cada item de lista como un único bloque para evitar
            // viñetas repetidas cuando hay enlaces/strong/comas en nodos separados.
            const textoLi = normalizarTextoParaPdf((nodo.textContent || '').replace(/\s+/g, ' '));
            if (textoLi) {
              bloques.push({
                texto: `- ${textoLi}`,
                tipo: 'lista',
                negrita: false,
                tamanoFuente: contexto.tamanoFuente || 10,
                color: contexto.color || '#000000',
                indentacion: 5
              });
              bloques.push({
                texto: '',
                tipo: 'salto',
                negrita: false,
                tamanoFuente: compacto ? 2 : 4
              });
            }
            return;
          }
            
          case 'br':
            bloques.push({
              texto: '',
              tipo: 'salto',
              negrita: false,
              tamanoFuente: compacto ? 6 : 10
            });
            return;

          case 'hr':
            // Separador visual: no dibujar guiones (en PDF parecían línea punteada).
            // Solo aire entre bloques; el consolidado ya tiene borde inferior en firmas-grupo.
            bloques.push({
              texto: '',
              tipo: 'salto',
              negrita: false,
              tamanoFuente: 14
            });
            return;

          case 'svg':
          case 'script':
          case 'style':
          case 'input':
          case 'button':
            return;

          case 'i': {
            const cls = nodo.getAttribute('class') || '';
            if (/\bfa[srb]?\b/i.test(cls) || /\bfa-(solid|regular|brands)\b/i.test(cls)) {
              return;
            }
            break;
          }
            
          case 'div':
          case 'span':
            // Extraer estilos inline si existen
            if (nodo.style.fontWeight === 'bold' || nodo.style.fontWeight === '700') {
              nuevoContexto.negrita = true;
            }
            if (nodo.style.color) {
              nuevoContexto.color = nodo.style.color;
            }
            break;
            
          default:
            // Otros tags no procesados específicamente
            break;
        }
        
        // Procesar hijos recursivamente
        for (const hijo of nodo.childNodes) {
          procesarNodo(hijo, nuevoContexto);
        }
        
        // Agregar salto después de ciertos elementos
        if (['p', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tagName)) {
          bloques.push({
            texto: '',
            tipo: 'salto',
            negrita: false,
            // En modo compacto, Enter en CKEditor (párrafos) no debe dejar tanto aire como un párrafo típico en Word.
            tamanoFuente: compacto ? 2 : 5
          });
        }
      }
    }
    
    // 4. Iniciar procesamiento desde la raíz
    for (const hijo of tempDiv.childNodes) {
      procesarNodo(hijo);
    }
    
    // 5. Limpiar bloques vacíos consecutivos
    const bloquesLimpios = [];
    let ultimoFueSalto = false;
    
    for (const bloque of bloques) {
      if (bloque.tipo === 'salto') {
        if (!ultimoFueSalto) {
          bloquesLimpios.push(bloque);
          ultimoFueSalto = true;
        }
      } else {
        bloquesLimpios.push({
          ...bloque,
          texto: normalizarTextoParaPdf(bloque.texto || ''),
        });
        ultimoFueSalto = false;
      }
    }
    
    // 6. Retornar bloques procesados (sin bloques de texto vacíos tras normalizar)
    return bloquesLimpios.filter(
      (b) => b.tipo === 'salto' || (b.texto && String(b.texto).trim().length > 0)
    );
    
  } catch (error) {
    console.error('Error convirtiendo HTML a texto:', error);
    const plano = normalizarTextoParaPdf(
      sanitizarHtmlParaPdf(html || '').replace(/<[^>]*>/g, ' ')
    );
    return [{
      texto: plano,
      tipo: 'parrafo',
      negrita: false,
      tamanoFuente: 10,
      color: '#000000',
      indentacion: 0
    }];
  }
}

/**
 * Convierte el encabezado HTML a imagen y la agrega al PDF
 * 
 * @param {jsPDF} doc - Instancia de jsPDF
 * @param {Object} encabezado - Objeto con {izquierda, centro, derecha}
 * @param {number} yPosition - Posición Y donde colocar el encabezado
 * @returns {Promise<number>} Nueva posición Y después del encabezado
 */
export async function procesarEncabezadoComoImagen(doc, encabezado, yPosition) {
  // 1. Crear elemento HTML temporal con el encabezado
  const tempDiv = document.createElement('div');
  tempDiv.style.width = '190mm';  // Ancho de página menos márgenes
  tempDiv.style.padding = '10px';
  tempDiv.style.backgroundColor = 'white';
  tempDiv.style.display = 'flex';
  tempDiv.style.justifyContent = 'space-between';
  
  // 2. Agregar las tres columnas
  tempDiv.innerHTML = `
    <div style="flex: 1; text-align: left;">
      ${encabezado.izquierda || ''}
    </div>
    <div style="flex: 1; text-align: center;">
      ${encabezado.centro || ''}
    </div>
    <div style="flex: 1; text-align: right;">
      ${encabezado.derecha || ''}
    </div>
  `;
  
  // 3. Agregar temporalmente al DOM (necesario para html2canvas)
  tempDiv.style.position = 'absolute';
  tempDiv.style.left = '-9999px';
  document.body.appendChild(tempDiv);
  
  // 4. Convertir a canvas usando html2canvas
  try {
    const canvas = await html2canvas(tempDiv, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff'
    });
    
    // 5. Convertir canvas a imagen base64
    const imagenBase64 = canvas.toDataURL('image/jpeg', 0.95);
    
    // 6. Calcular dimensiones para el PDF
    const anchoImagen = 190;  // mm
    const altoImagen = (canvas.height / canvas.width) * anchoImagen;
    
    // 7. Agregar imagen al PDF
    doc.addImage(
      imagenBase64,
      'JPEG',
      10,  // x (margen izquierdo)
      yPosition,
      anchoImagen,
      altoImagen
    );
    
    // 8. Limpiar elemento temporal
    document.body.removeChild(tempDiv);
    
    // 9. Retornar nueva posición Y
    return yPosition + altoImagen + 3;
    
  } catch (error) {
    console.error('Error procesando encabezado:', error);
    document.body.removeChild(tempDiv);
    
    // Fallback: renderizar como texto simple
    doc.setFontSize(10);
    doc.text('Encabezado no disponible', 10, yPosition);
    return yPosition + 10;
  }
}

/**
 * Agrega una firma como imagen al PDF
 * 
 * @param {jsPDF} doc - Instancia de jsPDF
 * @param {string} imagenBase64 - Imagen en formato base64
 * @param {number} yPosition - Posición Y donde colocar la firma
 * @param {Object} metadata - Metadatos de la firma (nombre, RM, etc.)
 * @returns {number} Nueva posición Y después de la firma
 */
export function agregarFirmaComoImagen(doc, imagenBase64, yPosition, metadata = {}, opciones = {}) {
  const margenIzq = 15;
  const margenDer = 15;
  const pageW = doc.internal.pageSize.getWidth();
  const anchoUtil = pageW - margenIzq - margenDer;
  const estiloEvolucion = !!(opciones.estiloEvolucion || metadata.estiloEvolucion);

  let y = yPosition;
  const titulo = metadata.tipo === 'profesional' ? 'FIRMA DEL PROFESIONAL' : 'FIRMA DEL PACIENTE';

  if (opciones.separadorPrevia) {
    doc.setDrawColor(233, 236, 239);
    doc.setLineWidth(0.35);
    doc.line(margenIzq, y + 1.5, margenIzq + anchoUtil, y + 1.5);
    y += 6;
  }

  if (estiloEvolucion) {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text(normalizarTextoParaPdf(titulo), margenIzq, y + 3);
    y += 7;
  } else {
    const hBanda = 5.5;
    doc.setFillColor(243, 243, 243);
    doc.setDrawColor(224, 224, 224);
    doc.setLineWidth(0.15);
    doc.rect(margenIzq, y, anchoUtil, hBanda, 'FD');
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text(normalizarTextoParaPdf(titulo), margenIzq + 2, y + 4);
    y += hBanda;

    doc.setDrawColor(224, 224, 224);
    doc.line(margenIzq, y, margenIzq + anchoUtil, y);
    y += 2;
  }

  const xTexto = margenIzq + 2;
  const anchoTextoMax = anchoUtil * 0.52;

  const lineas = [];
  if (metadata.tipo === 'profesional') {
    if (metadata.textoPieFirma) {
      metadata.textoPieFirma.split('\n').forEach((l) => {
        const t = normalizarTextoParaPdf(l);
        if (t) lineas.push(t);
      });
    } else {
      if (metadata.nombre) {
        lineas.push(normalizarTextoParaPdf(metadata.nombre).toUpperCase());
      }
      if (metadata.registroMedico) {
        let textoRM = 'RM: ' + normalizarTextoParaPdf(metadata.registroMedico);
        if (metadata.codigoHabilitacion && metadata.codigoHabilitacion !== 'N/A') {
          textoRM += ' | ' + normalizarTextoParaPdf(metadata.codigoHabilitacion);
        }
        lineas.push(textoRM);
      }
    }
  } else if (metadata.tipo === 'paciente') {
    if (metadata.nombrePaciente) {
      lineas.push(normalizarTextoParaPdf(metadata.nombrePaciente).toUpperCase());
    }
    if (metadata.documentoPaciente) {
      lineas.push(normalizarTextoParaPdf(metadata.documentoPaciente).toUpperCase());
    }
  }

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(108, 117, 125);

  let yLine = y + 2;
  let maxW = 0;
  lineas.forEach((linea) => {
    const wrapped = doc.splitTextToSize(linea, anchoTextoMax);
    wrapped.forEach((ln) => {
      doc.text(ln, xTexto, yLine);
      maxW = Math.max(maxW, doc.getTextWidth(ln));
      yLine += 3.2;
    });
  });

  const imgW = 46;
  const imgH = 23;
  const xImg = Math.min(margenIzq + anchoUtil - imgW - 2, xTexto + Math.max(maxW, 48) + 6);
  const yImg = y + 2;

  if (imagenBase64) {
    try {
      doc.addImage(imagenBase64, 'PNG', xImg, yImg, imgW, imgH);
    } catch (error) {
      console.error('Error agregando firma:', error);
    }
  } else if (metadata.tipo === 'paciente') {
    doc.setDrawColor(173, 181, 189);
    doc.setLineWidth(0.35);
    doc.line(xImg, yImg + 14, xImg + 42, yImg + 14);
  }

  const yFinTexto = lineas.length > 0 ? yLine + 2 : y + 6;
  const yFinImg = imagenBase64 ? yImg + imgH + 3 : yImg + 18;
  return Math.max(yFinTexto, yFinImg);
}

/**
 * Agrega una tabla con bandas grises usando jspdf-autotable
 * 
 * @param {jsPDF} doc - Instancia de jsPDF
 * @param {Array<Array<string>>} filas - Filas de la tabla
 * @param {number} yPosition - Posición Y inicial
 * @param {Object} estilos - Estilos personalizados (opcional)
 * @returns {number} Nueva posición Y después de la tabla
 */
export function agregarTablaConBandas(doc, filas, yPosition, estilos = {}) {
  // Padding compacto (jsPDF units, coherente con jspdf-autotable): menos altura por fila en fichas PDF.
  const padCeldaCompacto = { top: 0.6, bottom: 0.6, left: 1.2, right: 1.2 };
  // 1. Configuración por defecto
  const configDefault = {
    startY: yPosition,
    theme: 'plain',
    headStyles: {
      fillColor: [248, 249, 250],  // Gris claro
      textColor: [0, 0, 0],
      fontStyle: 'bold',
      fontSize: 9,
      cellPadding: padCeldaCompacto
    },
    bodyStyles: {
      fontSize: 9,
      cellPadding: padCeldaCompacto,
      textColor: [0, 0, 0],
      overflow: 'linebreak',
      valign: 'top'
    },
    alternateRowStyles: {
      fillColor: [248, 249, 250]  // Bandas grises
    },
    columnStyles: {
      0: { cellWidth: 52, fontStyle: 'bold', fillColor: [248, 249, 250] },
      1: { cellWidth: 'auto' }
    },
    margin: { left: 15, right: 15 },
    tableLineColor: [222, 226, 230],
    tableLineWidth: 0.1
  };
  
  // 2. Combinar configuración por defecto con estilos personalizados (merge profundo en body/head/columnas)
  const configFinal = {
    ...configDefault,
    ...estilos,
    headStyles: { ...configDefault.headStyles, ...estilos.headStyles },
    bodyStyles: {
      ...configDefault.bodyStyles,
      ...estilos.bodyStyles,
      cellPadding: {
        ...configDefault.bodyStyles.cellPadding,
        ...estilos.bodyStyles?.cellPadding
      }
    },
    alternateRowStyles: { ...configDefault.alternateRowStyles, ...estilos.alternateRowStyles },
    columnStyles: estilos.columnStyles
      ? { ...configDefault.columnStyles, ...estilos.columnStyles }
      : configDefault.columnStyles
  };
  
  const filasPdf = (filas || []).map((fila) =>
    fila.map((celda) => prepararTextoCeldaTablaPdf(celda))
  );

  // 3. Generar tabla usando jspdf-autotable
  doc.autoTable({
    body: filasPdf,
    ...configFinal
  });
  
  // 4. Obtener posición Y final de la tabla
  const yFinal = doc.lastAutoTable.finalY;
  
  // 5. Retornar nueva posición Y
  return yFinal;
}

/**
 * Verifica si hay espacio suficiente en la página actual
 * Si no hay espacio, crea una nueva página
 * 
 * @param {jsPDF} doc - Instancia de jsPDF
 * @param {number} yActual - Posición Y actual
 * @param {number} alturaRequerida - Altura del contenido a agregar
 * @param {number} margenInferior - Margen inferior de la página
 * @returns {number} Posición Y ajustada (puede ser en nueva página)
 */
export function aplicarPaginacionInteligente(doc, yActual, alturaRequerida, margenInferior, margenSuperior = 20) {
  // 1. Obtener altura total de la página
  const altoTotal = doc.internal.pageSize.getHeight();
  
  // 2. Calcular espacio disponible en página actual
  const espacioDisponible = altoTotal - yActual - margenInferior;
  
  // 3. Verificar si el contenido cabe en la página actual
  if (alturaRequerida > espacioDisponible) {
    // 3.1 No cabe: crear nueva página
    doc.addPage();
    
    // 3.2 Retornar posición Y inicial de nueva página (alineado con márgenes del documento)
    return margenSuperior;
  } else {
    // 3.3 Sí cabe: mantener posición actual
    return yActual;
  }
}

/**
 * Agrega foliación a todas las páginas del documento
 * 
 * @param {jsPDF} doc - Instancia de jsPDF
 * @param {number} margenInferior - Margen inferior en mm
 */
export function agregarFoliacion(doc, margenInferior) {
  // 1. Obtener número total de páginas
  const totalPaginas = doc.internal.getNumberOfPages();
  
  // 2. Obtener dimensiones de la página
  const anchoTotal = doc.internal.pageSize.getWidth();
  const altoTotal = doc.internal.pageSize.getHeight();
  
  // 3. Calcular posición Y del folio
  const yFolio = altoTotal - (margenInferior / 2);
  
  // 4. Iterar sobre todas las páginas
  for (let i = 1; i <= totalPaginas; i++) {
    // 4.1 Ir a la página i
    doc.setPage(i);
    
    // 4.2 Configurar estilo del folio
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);  // Gris
    doc.setFont('helvetica', 'normal');
    
    // 4.3 Crear texto del folio
    const textoFolio = normalizarTextoParaPdf(`Página ${i} de ${totalPaginas}`);
    
    // 4.4 Agregar texto centrado
    doc.text(
      textoFolio,
      anchoTotal / 2,
      yFolio,
      { align: 'center' }
    );
  }
}

/**
 * Longitud aproximada del texto visible (sin etiquetas HTML).
 * El HTML de editores infla mucho el string y rompía la paginación previa al bloque "texto".
 */
function longitudTextoPlanoAprox(html) {
  if (!html || typeof html !== 'string') return 0;
  return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().length;
}

/** Parte tokens muy largos sin espacios para que splitTextToSize no desborde el ancho útil. */
function partirCadenaLargaParaPdf(texto, chunkSize = 42) {
  if (!texto) return '';
  return String(texto).replace(/(\S{43,})/g, (token) => {
    const trozos = [];
    for (let i = 0; i < token.length; i += chunkSize) {
      trozos.push(token.slice(i, i + chunkSize));
    }
    return trozos.join('\n');
  });
}

const EVOL_META_LABEL_W_MM = 28;
const EVOL_META_COL_GAP_MM = 10;

/**
 * Dibuja una fila etiqueta-valor (metadata de evolución) y devuelve la Y final.
 */
function dibujarFilaMetadataEvol(doc, x, y, anchoCol, etiqueta, valor, fs) {
  const labelW = EVOL_META_LABEL_W_MM;
  const valueW = Math.max(10, anchoCol - labelW);
  const lineH = fs * 0.42;
  doc.setFontSize(fs);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text(normalizarTextoParaPdf(etiqueta), x, y);
  doc.setFont('helvetica', 'normal');
  const valNorm = partirCadenaLargaParaPdf(normalizarTextoParaPdf(String(valor ?? '')));
  const lines = doc.splitTextToSize(valNorm, valueW);
  let yVal = y;
  lines.forEach((ln, i) => {
    doc.text(ln, x + labelW, yVal);
    if (i < lines.length - 1) {
      yVal += lineH;
    }
  });
  const totalH = Math.max(lineH, lines.length * lineH);
  return y + totalH + 2;
}

/**
 * Cuadrícula 2×2: paciente/documento | profesional/fecha (mismo layout que vista HTML).
 */
export function agregarMetadataSuperiorEvolucion(doc, datos, yPosition, margenes, estilos = {}) {
  const fs = estilos.fontSize || 10;
  const pageW = doc.internal.pageSize.getWidth();
  const anchoUtil = pageW - margenes.left - margenes.right;
  const colW = (anchoUtil - EVOL_META_COL_GAP_MM) / 2;
  const xCol1 = margenes.left;
  const xCol2 = margenes.left + colW + EVOL_META_COL_GAP_MM;
  let yStart = yPosition;

  const yAfterPac = dibujarFilaMetadataEvol(doc, xCol1, yStart, colW, 'Paciente:', datos.paciente, fs);
  const yAfterDoc = dibujarFilaMetadataEvol(doc, xCol2, yStart, colW, 'Documento:', datos.documento, fs);
  const yRow2 = Math.max(yAfterPac, yAfterDoc);

  const yAfterProf = dibujarFilaMetadataEvol(doc, xCol1, yRow2, colW, 'Profesional:', datos.profesional, fs);
  const yAfterFecha = dibujarFilaMetadataEvol(doc, xCol2, yRow2, colW, 'Fecha y hora:', datos.fechaHora, fs);

  return Math.max(yAfterProf, yAfterFecha) + (estilos.espacioDespuesMm ?? 1);
}

/**
 * Estima la altura de una sección para paginación
 * 
 * @param {Object} seccion - Sección del documento
 * @returns {number} Altura estimada en mm
 */
export function estimarAlturaSeccion(seccion) {
  // Estimación conservadora de altura para paginación
  
  switch (seccion.tipo) {
    case 'salto-pagina':
      return 0;  // No ocupa espacio, solo crea nueva página
      
    case 'titulo-seccion':
      return 10;  // Altura para títulos de sección
      
    case 'subtitulo': {
      const extraTop = seccion.estilos?.margenSuperiorMm ?? 0;
      const extraBottom = seccion.estilos?.espacioDespuesMm ?? 8;
      return extraTop + extraBottom + 6;
    }
      
    case 'banner':
      return 8;  // Franja + aire (banner real ~6–7 mm)
      
    case 'tabla':
    case 'tabla-datos': {
      const numFilas = seccion.datos ? seccion.datos.filas.length : 1;
      return numFilas * 5.5 + 4;  // Filas compactas (padding/tipo 9) + margen posterior
    }
      
    case 'rule': {
      const mSup = seccion.estilos?.margenSuperiorMm ?? 2;
      const mInf = seccion.estilos?.espacioDespuesMm ?? 3;
      return mSup + 2 + mInf;
    }

    case 'cabecera-profesional-fecha': {
      const mSup = seccion.estilos?.margenSuperiorMm ?? 0;
      const mInf = seccion.estilos?.espacioDespuesMm ?? 2;
      const fs = seccion.estilos?.fontSize || 10;
      return mSup + fs * 0.45 + mInf + 1;
    }

    case 'metadata-superior-evolucion': {
      const mSup = seccion.estilos?.margenSuperiorMm ?? 0;
      const mInf = seccion.estilos?.espacioDespuesMm ?? 2;
      const fs = seccion.estilos?.fontSize || 10;
      return mSup + fs * 0.42 * 4 + 8 + mInf;
    }

    case 'texto': {
      // Usar texto plano, no el HTML crudo (evita saltos de página en blanco).
      // El cuerpo se dibuja línea a línea con paginación real; aquí solo hace falta
      // un tope razonable para decidir si cabe *iniciar* la sección en la página actual.
      const longitudTexto = longitudTextoPlanoAprox(seccion.contenido);
      const lineasEstimadas = Math.max(1, Math.ceil(longitudTexto / 85));
      const alturaMm = lineasEstimadas * 4.2;
      const maxPreChequeo = 55;
      return Math.min(alturaMm, maxPreChequeo);
    }
      
    case 'firma':
    case 'firmas-grupo':
      return 40;  // Altura estimada para firmas
      
    default:
      return 20;  // Valor por defecto conservador
  }
}

/**
 * Genera un PDF con texto nativo usando jsPDF
 * 
 * @param {Object} config - Configuración del documento
 * @param {string} config.filename - Nombre del archivo PDF
 * @param {Object} config.paciente - Datos del paciente
 * @param {string} config.paciente.nombreCompleto - Nombre completo del paciente
 * @param {string} config.paciente.documentoCompleto - Documento del paciente
 * @param {Object|null} config.encabezadoPersonalizado - Encabezado personalizado (opcional)
 * @param {string} config.encabezadoPersonalizado.izquierda - HTML del lado izquierdo
 * @param {string} config.encabezadoPersonalizado.centro - HTML del centro
 * @param {string} config.encabezadoPersonalizado.derecha - HTML del lado derecho
 * @param {Array<Object>} config.secciones - Secciones del documento
 * @param {string} config.secciones[].titulo - Título de la sección
 * @param {string} config.secciones[].contenido - Contenido (puede ser HTML o texto)
 * @param {string} config.secciones[].tipo - Tipo: 'texto', 'tabla', 'banner', 'tabla-datos', 'rule', 'cabecera-profesional-fecha', 'subtitulo', etc.
 * @param {Object} config.secciones[].estilos - Estilos específicos (opcional)
 * @param {Array<Object>} config.firmas - Firmas a incluir (opcional)
 * @param {string} config.firmas[].tipo - 'profesional' o 'paciente'
 * @param {string} config.firmas[].imagenBase64 - Imagen de la firma en base64
 * @param {Object} config.firmas[].metadata - Metadatos (nombre, RM, etc.)
 * @param {Object} config.margenes - Márgenes del documento (opcional)
 * @param {number} config.margenes.top - Margen superior en mm (default: 20)
 * @param {number} config.margenes.bottom - Margen inferior en mm (default: 20)
 * @param {number} config.margenes.left - Margen izquierdo en mm (default: 15)
 * @param {number} config.margenes.right - Margen derecho en mm (default: 15)
 * @param {boolean} [config.omitTablaPacienteInicial] - Si es true, no inserta la tabla PACIENTE/DOCUMENTO
 *   automática (útil cuando esas filas van en config.secciones con otro orden).
 * 
 * @returns {Promise<jsPDF>} Objeto jsPDF con el documento generado
 */
export async function generarPdfConTextoNativo(config) {
  // Validación de datos requeridos
  if (!config.paciente) {
    console.error('Datos del paciente no disponibles');
    throw new Error('Datos del paciente son requeridos');
  }
  
  if (!config.secciones || config.secciones.length === 0) {
    console.error('No hay secciones para generar el PDF');
    throw new Error('Se requiere al menos una sección');
  }
  
  // 1. Inicializar documento jsPDF
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'letter'
  });
  
  // 2. Configurar márgenes (con valores por defecto)
  const margenes = config.margenes || {
    top: 20,
    bottom: 20,
    left: 15,
    right: 15
  };
  
  // 3. Calcular dimensiones útiles
  const anchoUtil = doc.internal.pageSize.getWidth() - margenes.left - margenes.right;
  // const altoUtil = doc.internal.pageSize.getHeight() - margenes.top - margenes.bottom; // No usado actualmente
  
  // 4. Posición Y inicial
  let yActual = margenes.top;
  
  // 5. Procesar encabezado personalizado (si existe)
  console.log('🔍 [pdfHelpers] Verificando encabezado personalizado:', !!config.encabezadoPersonalizado);
  if (config.encabezadoPersonalizado) {
    console.log('📋 [pdfHelpers] Datos del encabezado:', config.encabezadoPersonalizado);
    try {
      console.log('🖼️ [pdfHelpers] Procesando encabezado como imagen...');
      yActual = await procesarEncabezadoComoImagen(
        doc, 
        config.encabezadoPersonalizado, 
        yActual
      );
      console.log('✅ [pdfHelpers] Encabezado procesado exitosamente. Nueva posición Y:', yActual);
      yActual += 5;  // Espacio después del encabezado
    } catch (error) {
      console.error('❌ [pdfHelpers] Error procesando encabezado personalizado:', error);
      // Continuar sin encabezado
    }
  }
  
  // 6. Agregar información del paciente (salvo modo certificado combinado u otros layouts custom)
  if (!config.omitTablaPacienteInicial) {
    yActual = agregarTablaConBandas(
      doc,
      [
        ['PACIENTE', config.paciente.nombreCompleto],
        ['DOCUMENTO', config.paciente.documentoCompleto]
      ],
      yActual,
      {
        columnStyles: {
          0: { cellWidth: 40, fontStyle: 'bold', fillColor: [248, 249, 250] },
          1: { cellWidth: 'auto', fillColor: [255, 255, 255] }
        }
      }
    );
    yActual += 1.5;  // Espacio tras bloque PACIENTE/DOCUMENTO (ficha consolidada más densa)
  }

  // 7. Procesar cada sección del documento
  for (const seccion of config.secciones) {
    // 7.1 Verificar espacio disponible
    const alturaEstimada = estimarAlturaSeccion(seccion);
    yActual = aplicarPaginacionInteligente(
      doc, 
      yActual, 
      alturaEstimada, 
      margenes.bottom,
      margenes.top
    );
    
    // 7.2 Renderizar según tipo de sección
    switch (seccion.tipo) {
      case 'salto-pagina': {
        // Si el contenido previo llenó la hoja, aplicarPaginacionInteligente(..., 0) ya abrió página
        // (0 > espacioDisponible cuando y quedó bajo el margen inferior). Un segundo addPage aquí
        // deja una hoja en blanco entre evoluciones.
        // Margen amplio: tras el salto, el bucle suma ~3 mm y pueden quedar secciones cortas cerca del tope.
        const cercaDelTope = yActual <= margenes.top + 15;
        if (!cercaDelTope) {
          doc.addPage();
        }
        yActual = margenes.top;
        break;
      }
        
      case 'titulo-seccion':
        // Título de sección (más grande que subtítulo)
        doc.setFontSize(seccion.estilos?.fontSize || 14);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(50, 94, 104);  // #325e68
        doc.text(normalizarTextoParaPdf(seccion.contenido), margenes.left, yActual);
        yActual += 10;  // Más espacio después del título
        break;
        
      case 'subtitulo': {
        const mSupSub = seccion.estilos?.margenSuperiorMm ?? 0;
        if (mSupSub > 0) {
          yActual += mSupSub;
        }
        const fsSub = seccion.estilos?.fontSize || 12;
        doc.setFontSize(fsSub);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(50, 94, 104);  // #325e68
        const textoSub = normalizarTextoParaPdf(seccion.contenido);
        if (seccion.estilos?.alineacion === 'center') {
          const pageW = doc.internal.pageSize.getWidth();
          doc.text(textoSub, pageW / 2, yActual, { align: 'center' });
        } else {
          doc.text(textoSub, margenes.left, yActual);
        }
        yActual += seccion.estilos?.espacioDespuesMm ?? 8;
        break;
      }

      case 'rule': {
        const mSupRule = seccion.estilos?.margenSuperiorMm ?? 2;
        yActual += mSupRule;
        doc.setDrawColor(206, 212, 218);
        doc.setLineWidth(0.35);
        const xRule1 = margenes.left;
        const xRule2 = doc.internal.pageSize.getWidth() - margenes.right;
        doc.line(xRule1, yActual, xRule2, yActual);
        yActual += seccion.estilos?.espacioDespuesMm ?? 3;
        break;
      }

      case 'metadata-superior-evolucion': {
        const mSupMeta = seccion.estilos?.margenSuperiorMm ?? 0;
        if (mSupMeta > 0) {
          yActual += mSupMeta;
        }
        const fsMeta = seccion.estilos?.fontSize || 10;
        const altoMetaEst = fsMeta * 0.42 * 4 + 8;
        yActual = aplicarPaginacionInteligente(doc, yActual, altoMetaEst, margenes.bottom, margenes.top);
        yActual = agregarMetadataSuperiorEvolucion(
          doc,
          {
            paciente: seccion.paciente ?? '',
            documento: seccion.documento ?? '',
            profesional: seccion.profesional ?? '',
            fechaHora: seccion.fechaHora ?? ''
          },
          yActual,
          margenes,
          seccion.estilos || {}
        );
        break;
      }

      case 'cabecera-profesional-fecha': {
        const mSupCf = seccion.estilos?.margenSuperiorMm ?? 0;
        yActual += mSupCf;
        const fsCf = seccion.estilos?.fontSize || 10;
        doc.setFontSize(fsCf);
        const altoLineaCf = fsCf * 0.45 + 1;
        yActual = aplicarPaginacionInteligente(doc, yActual, altoLineaCf, margenes.bottom, margenes.top);

        const prof = normalizarTextoParaPdf(String(seccion.profesional ?? '')).toUpperCase();
        const fh = normalizarTextoParaPdf(String(seccion.fechaHora ?? ''));
        const lab1 = normalizarTextoParaPdf('PROFESIONAL:');
        const lab2 = normalizarTextoParaPdf('FECHA Y HORA:');
        const sep = normalizarTextoParaPdf('|');

        const yLine = yActual;
        const anchoDisponible = doc.internal.pageSize.getWidth() - margenes.left - margenes.right;
        let xMitad = margenes.left + (anchoDisponible / 2);

        // Profesional a la izquierda
        let xPos = margenes.left;
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text(lab1, xPos, yLine);
        xPos += doc.getTextWidth(lab1) + 2;
        doc.setFont('helvetica', 'normal');
        doc.text(prof, xPos, yLine);
        xPos += doc.getTextWidth(prof);

        // Si el nombre del profesional es muy largo, ajustamos el centro
        if (xPos + 10 > xMitad) {
            xMitad = xPos + 10;
        }

        // Separador dinamico
        doc.setTextColor(200, 200, 200);
        doc.text(sep, xMitad, yLine, { align: 'center' });

        // Fecha y hora a la derecha
        let xRight = xMitad + 10;
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text(lab2, xRight, yLine);
        xRight += doc.getTextWidth(lab2) + 2;
        doc.setFont('helvetica', 'normal');
        doc.text(fh, xRight, yLine);

        yActual += altoLineaCf + (seccion.estilos?.espacioDespuesMm ?? 1);
        break;
      }
        
      case 'banner': {
        // Aire opcional antes de la franja (certificados, títulos de sección)
        const mSupBanner = seccion.estilos?.margenSuperiorMm ?? 0;
        if (mSupBanner > 0) {
          yActual += mSupBanner;
        }
        // Franja de sección más baja que antes (ahorra hoja en fichas largas)
        const hBanner = seccion.estilos?.alturaBannerMm ?? 5.5;
        const yTextoBanner = seccion.estilos?.bannerTextoBaselineMm ?? 3.8;
        const extraTrasBanner = seccion.estilos?.espacioDespuesBannerMm ?? 1;
        doc.setFillColor(233, 236, 239);  // #e9ecef
        doc.rect(margenes.left, yActual, anchoUtil, hBanner, 'F');
        doc.setFontSize(seccion.estilos?.fontSize ?? 10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text(
          normalizarTextoParaPdf(seccion.titulo || seccion.contenido),
          margenes.left + 2,
          yActual + yTextoBanner
        );
        yActual += hBanner + extraTrasBanner;
        break;
      }
        
      case 'tabla': {
        const mSupT = seccion.estilos?.margenSuperiorMm ?? 0;
        if (mSupT > 0) {
          yActual += mSupT;
        }
        // Tabla simple con bandas
        yActual = agregarTablaConBandas(
          doc,
          seccion.datos ? seccion.datos.filas : [[seccion.titulo, seccion.contenido]],
          yActual,
          seccion.estilos
        );
        yActual += seccion.estilos?.postTablaMm ?? 2;
        break;
      }
        
      case 'tabla-datos': {
        const mSupTabla = seccion.estilos?.margenSuperiorMm ?? 0;
        if (mSupTabla > 0) {
          yActual += mSupTabla;
        }
        // Tabla con etiqueta-valor
        if (seccion.datos && seccion.datos.filas) {
          const filasTabla = seccion.datos.filas.map(fila => [
            fila.etiqueta,
            fila.valor
          ]);
          yActual = agregarTablaConBandas(
            doc,
            filasTabla,
            yActual,
            seccion.estilos
          );
        }
        // Espacio tras tabla (por defecto más corto que antes para fichas tipo historia consolidada)
        yActual += seccion.estilos?.postTablaMm ?? 2;
        break;
      }
        
      case 'texto':
        // Separación explícita: el HTML no aporta margen al pasar por convertirHtmlATextoPlano
        yActual += seccion.estilos?.margenSuperiorMm ?? 5;
        if (seccion.titulo) {
          yActual = aplicarPaginacionInteligente(doc, yActual, 10, margenes.bottom, margenes.top);
          doc.setFontSize(11);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(50, 94, 104);
          doc.text(normalizarTextoParaPdf(String(seccion.titulo)), margenes.left, yActual);
          yActual += 5;
        }
        const compactoHtml = !!seccion.estilos?.htmlTextoCompacto;
        const bloques = convertirHtmlATextoPlano(seccion.contenido || '', {
          compacto: compactoHtml
        });
        const factorSalto =
          typeof seccion.estilos?.factorSaltoLineaPdf === 'number'
            ? seccion.estilos.factorSaltoLineaPdf
            : compactoHtml
              ? 0.28
              : 0.4;
        const espacioPostBloque =
          typeof seccion.estilos?.espacioPostBloqueTextoMm === 'number'
            ? seccion.estilos.espacioPostBloqueTextoMm
            : compactoHtml
              ? 0.35
              : 2;
        
        // Renderizar cada bloque
        for (const bloque of bloques) {
          if (bloque.tipo === 'salto') {
            yActual += bloque.tamanoFuente * factorSalto;
            continue;
          }
          
          // Configurar estilo
          doc.setFontSize(bloque.tamanoFuente || 10);
          doc.setFont('helvetica', bloque.negrita ? 'bold' : 'normal');
          
          if (bloque.color && bloque.color !== '#000000') {
            // Convertir color hex a RGB
            const r = parseInt(bloque.color.slice(1, 3), 16);
            const g = parseInt(bloque.color.slice(3, 5), 16);
            const b = parseInt(bloque.color.slice(5, 7), 16);
            doc.setTextColor(r, g, b);
          } else {
            doc.setTextColor(0, 0, 0);
          }
          
          // Agregar texto con word wrap (paginar antes de cada línea: evita desbordar la página)
          const lineas = doc.splitTextToSize(
            partirCadenaLargaParaPdf(bloque.texto),
            anchoUtil - (bloque.indentacion || 0)
          );
          
          const altoLinea = (bloque.tamanoFuente || 10) * 0.4;
          for (const linea of lineas) {
            yActual = aplicarPaginacionInteligente(
              doc,
              yActual,
              altoLinea + 0.5,
              margenes.bottom,
              margenes.top
            );
            doc.text(
              linea,
              margenes.left + (bloque.indentacion || 0),
              yActual
            );
            yActual += altoLinea;
          }
          
          yActual += espacioPostBloque;
        }
        break;
        
      case 'firma': {
        const mSupFirma = seccion.estilos?.margenSuperiorMm ?? 0;
        if (mSupFirma > 0) {
          yActual += mSupFirma;
        }
        // Procesar firma individual como parte de la sección
        try {
          yActual = agregarFirmaComoImagen(
            doc,
            seccion.imagenBase64,
            yActual,
            seccion.metadata || {},
            {
              estiloEvolucion: seccion.estilos?.estiloEvolucion,
              separadorPrevia: seccion.estilos?.separadorPrevia
            }
          );
          yActual += seccion.estilos?.estiloEvolucion ? 3 : 5;
        } catch (error) {
          console.error('Error agregando firma en sección:', error);
        }
        break;
      }
        
      case 'firmas-grupo':
        try {
          if (seccion.estilos?.estiloEvolucion) {
            const firmasEvol = seccion.firmas || [];
            firmasEvol.forEach((firma, idx) => {
              yActual = agregarFirmaComoImagen(
                doc,
                firma.imagenBase64,
                yActual,
                firma.metadata || {},
                {
                  estiloEvolucion: true,
                  separadorPrevia: seccion.estilos?.separadorPrevia !== false
                    && (idx === 0 || !!seccion.estilos?.separadorCadaFirma)
                }
              );
              yActual += 3;
            });
          } else {
            yActual = agregarFirmasLadoALado(
              doc,
              seccion.firmas || [],
              yActual
            );
            yActual += 5;
          }
        } catch (error) {
          console.error('Error agregando grupo de firmas:', error);
        }
        break;
        
      default:
        // Tipo de sección no reconocido
        break;
    }
    
    yActual += 3;  // Espacio entre secciones
  }
  
  // 8. Agregar firmas (si existen)
  if (config.firmas && config.firmas.length > 0) {
    // Asegurar que las firmas no se dividan entre páginas
    const alturaFirmas = config.firmas.length * 35;  // Estimación
    yActual = aplicarPaginacionInteligente(
      doc,
      yActual,
      alturaFirmas,
      margenes.bottom,
      margenes.top
    );
    
    for (const firma of config.firmas) {
      try {
        yActual = agregarFirmaComoImagen(
          doc,
          firma.imagenBase64,
          yActual,
          firma.metadata
        );
        yActual += 5;  // Espacio entre firmas
      } catch (error) {
        console.error('Error agregando firma:', error);
        // Continuar con las demás firmas
      }
    }
  }
  
  // 9. Agregar foliación a todas las páginas
  agregarFoliacion(doc, margenes.bottom);
  
  // 10. Retornar documento generado
  return doc;
}


/**
 * Agregar firmas lado a lado (profesional y paciente en la misma fila)
 * 
 * @param {jsPDF} doc - Instancia de jsPDF
 * @param {Array} firmas - Array de objetos de firma con imagenBase64 y metadata
 * @param {number} yPosition - Posición Y inicial
 * @returns {number} Nueva posición Y después de las firmas
 */
export function agregarFirmasLadoALado(doc, firmas, yPosition) {
  if (!firmas || firmas.length === 0) {
    return yPosition;
  }
  
  // Separar firmas por tipo
  const firmaProfesional = firmas.find(f => f.metadata && f.metadata.tipo === 'profesional');
  const firmaPaciente = firmas.find(f => f.metadata && f.metadata.tipo === 'paciente');
  
  // Si solo hay una firma, usar la función normal
  if (firmas.length === 1) {
    return agregarFirmaComoImagen(doc, firmas[0].imagenBase64, yPosition, firmas[0].metadata || {});
  }
  
  // Configuración de columnas
  const margenIzq = 15;
  const margenDer = 15;
  const pageW = doc.internal.pageSize.getWidth();
  const anchoUtil = pageW - margenIzq - margenDer;
  const espacioEntreColumnas = 5;
  const anchoColumna = (anchoUtil - espacioEntreColumnas) / 2;
  const xProf = margenIzq;
  const xPac = margenIzq + anchoColumna + espacioEntreColumnas;

  const headerH = 5.5;
  const lineGray = [222, 226, 230];

  const dibujarBandaTitulo = (x, y, w, titulo) => {
    doc.setFillColor(243, 243, 243);
    doc.setDrawColor(224, 224, 224);
    doc.setLineWidth(0.15);
    doc.rect(x, y, w, headerH, 'FD');
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text(normalizarTextoParaPdf(titulo), x + 2, y + 4);
    doc.setDrawColor(lineGray[0], lineGray[1], lineGray[2]);
    doc.setLineWidth(0.2);
    doc.line(x, y + headerH, x + w, y + headerH);
  };

  const yStart = yPosition;

  if (firmaProfesional) {
    dibujarBandaTitulo(xProf, yStart, anchoColumna, 'FIRMA DEL PROFESIONAL');
  }
  if (firmaPaciente) {
    dibujarBandaTitulo(xPac, yStart, anchoColumna, 'FIRMA DEL PACIENTE');
  }

  const yBody = yStart + headerH + 2;
  const imgW = 46;
  const imgH = 23;
  const textoMaxWProf = anchoColumna * 0.52;
  const textoMaxWPac = anchoColumna * 0.52;

  const dibujarBloqueTextoGris = (xBase, y0, maxW, lineasRaw) => {
    if (!lineasRaw.length) {
      return y0;
    }
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(108, 117, 125);
    let yl = y0;
    for (const raw of lineasRaw) {
      const partes = doc.splitTextToSize(raw, maxW);
      for (const ln of partes) {
        doc.text(ln, xBase, yl);
        yl += 3.1;
      }
    }
    return yl;
  };

  let yEndProf = yBody;
  let yEndPac = yBody;

  if (firmaProfesional) {
    const meta = firmaProfesional.metadata || {};
    let lineas = [];
    if (meta.textoPieFirma) {
      lineas = meta.textoPieFirma.split('\n').map((l) => normalizarTextoParaPdf(l)).filter(Boolean);
    } else {
      if (meta.nombre) {
        lineas.push(normalizarTextoParaPdf(meta.nombre).toUpperCase());
      }
      if (meta.registroMedico) {
        let t = 'RM: ' + normalizarTextoParaPdf(meta.registroMedico);
        if (meta.codigoHabilitacion) {
          t += ' | Hab: ' + normalizarTextoParaPdf(meta.codigoHabilitacion);
        }
        lineas.push(t);
      }
    }
    const yAfterText = dibujarBloqueTextoGris(xProf + 2, yBody + 2, textoMaxWProf - 2, lineas);
    const xImg = xProf + anchoColumna - imgW - 2;
    const yImg = yBody + 2;
    if (firmaProfesional.imagenBase64) {
      try {
        doc.addImage(firmaProfesional.imagenBase64, 'PNG', xImg, yImg, imgW, imgH);
      } catch (error) {
        console.error('Error agregando imagen de firma profesional:', error);
      }
    }
    yEndProf = Math.max(yAfterText, yImg + imgH + 2);
  }
  
  if (firmaPaciente) {
    const meta = firmaPaciente.metadata || {};
    const lineas = [];
    if (meta.nombrePaciente) {
      lineas.push(normalizarTextoParaPdf(meta.nombrePaciente).toUpperCase());
    }
    if (meta.documentoPaciente) {
      lineas.push(normalizarTextoParaPdf(meta.documentoPaciente).toUpperCase());
    }
    const yAfterText = dibujarBloqueTextoGris(xPac + 2, yBody + 2, textoMaxWPac - 2, lineas);
    const xImg = xPac + anchoColumna - imgW - 2;
    const yImg = yBody + 2;
    if (firmaPaciente.imagenBase64) {
      try {
        doc.addImage(firmaPaciente.imagenBase64, 'PNG', xImg, yImg, imgW, imgH);
      } catch (error) {
        console.error('Error agregando firma paciente:', error);
      }
    } else {
      doc.setDrawColor(173, 181, 189);
      doc.setLineWidth(0.4);
      const yLine = yBody + 16;
      doc.line(xImg, yLine, xImg + 42, yLine);
    }
    yEndPac = Math.max(yAfterText, yImg + imgH + 2, yBody + 20);
  }
  
  const yBottom = Math.max(yEndProf, yEndPac, yBody + 26);
  doc.setDrawColor(lineGray[0], lineGray[1], lineGray[2]);
  doc.setLineWidth(0.45);
  doc.line(margenIzq, yBottom, margenIzq + anchoUtil, yBottom);

  return yBottom + 4;
}
